/**
 * Swiper + video autopilot helpers (KINDW-832: loop carousels, e.g. CF-011).
 *
 * @override
 * - Do not modify vendor hooks under `vendors/*`.
 * - This module overrides `@amblique/base/app/hooks/use-swiper-video-carousel-autoplay` via the overlay resolver.
 */

import {useEffect, useRef} from 'react';

/**
 * While Swiper is moving, suppress IntersectionObserver "leave" so autopilot does not pause mid-gesture.
 *
 * @param {object|null|undefined} swiper - Swiper instance from useSwiper()
 * @param {boolean} enabled
 * @param {React.MutableRefObject<boolean>} suppressLeaveRef
 */
export function useSwiperAutoplaySuppress(swiper, enabled, suppressLeaveRef) {
  const clearRafRef = useRef(null);

  useEffect(() => {
    if (!swiper || !enabled) {
      return undefined;
    }

    const cancelScheduledClear = () => {
      if (clearRafRef.current !== null) {
        cancelAnimationFrame(clearRafRef.current);
        clearRafRef.current = null;
      }
    };

    const markInteracting = () => {
      cancelScheduledClear();
      suppressLeaveRef.current = true;
    };

    const tryClearInteracting = () => {
      cancelScheduledClear();

      const maxFrames = 48;
      let stableFrames = 0;
      let frames = 0;

      const tick = () => {
        clearRafRef.current = null;
        frames += 1;

        if (swiper.destroyed || frames > maxFrames) {
          suppressLeaveRef.current = false;

          return;
        }

        if (!swiper.animating) {
          stableFrames += 1;

          if (stableFrames >= 2) {
            suppressLeaveRef.current = false;

            return;
          }
        } else {
          stableFrames = 0;
        }

        clearRafRef.current = requestAnimationFrame(tick);
      };

      clearRafRef.current = requestAnimationFrame(() => {
        clearRafRef.current = requestAnimationFrame(tick);
      });
    };

    const eventsMark = [
      'touchStart',
      'touchMove',
      'touchMoveOpposite',
      'sliderMove',
      'sliderFirstMove',
      'transitionStart',
      'slideChangeTransitionStart',
      'beforeTransitionStart',
      'beforeLoopFix',
    ];

    const eventsClear = [
      'touchEnd',
      'transitionEnd',
      'slideChangeTransitionEnd',
      'slideNextTransitionEnd',
      'slidePrevTransitionEnd',
      'loopFix',
    ];

    eventsMark.forEach((name) => swiper.on(name, markInteracting));
    eventsClear.forEach((name) => swiper.on(name, tryClearInteracting));

    const el = swiper.el;
    const onPointerDown = () => markInteracting();
    const onPointerUp = () => tryClearInteracting();

    el?.addEventListener?.('pointerdown', onPointerDown, true);
    el?.addEventListener?.('pointerup', onPointerUp, true);
    el?.addEventListener?.('pointercancel', onPointerUp, true);

    return () => {
      cancelScheduledClear();

      eventsMark.forEach((name) => swiper.off(name, markInteracting));
      eventsClear.forEach((name) => swiper.off(name, tryClearInteracting));

      el?.removeEventListener?.('pointerdown', onPointerDown, true);
      el?.removeEventListener?.('pointerup', onPointerUp, true);
      el?.removeEventListener?.('pointercancel', onPointerUp, true);
    };
  }, [swiper, enabled, suppressLeaveRef]);
}

/**
 * Play when this video sits inside Swiper's active slide.
 * Resolves the active strip via .swiper-slide-active inside wrapperEl, then falls back to slides[activeIndex].
 *
 * @param {object|null|undefined} swiper
 * @param {boolean} enabled
 * @param {React.RefObject<HTMLElement|null>} containerRef
 * @param {object|null} video - AbstractVideo from useVideoPlayer
 * @param {() => Promise<unknown>} init
 */
export function useSwiperActiveSlideDomPlay(swiper, enabled, containerRef, video, init) {
  useEffect(() => {
    if (!swiper || !enabled) {
      return undefined;
    }

    let generation = 0;

    const isVideoInsideActiveSlide = () => {
      const root = containerRef.current;

      if (!root) {
        return false;
      }

      // Prefer the slide Swiper marks active in the DOM. swiper.slides[activeIndex] can disagree
      // with the painted active slide (React/chakra timing, thumbs sync, loop internals).
      const scope = swiper.wrapperEl ?? swiper.el;
      const activeSlideEl = scope?.querySelector?.('.swiper-slide-active');

      if (activeSlideEl?.contains(root)) {
        return true;
      }

      const activeSlide = swiper.slides?.[swiper.activeIndex];

      return Boolean(activeSlide?.contains(root));
    };

    const tryPlayIfActiveSlide = () => {
      generation += 1;
      const gen = generation;

      requestAnimationFrame(() => {
        if (!isVideoInsideActiveSlide()) {
          return;
        }

        void (async () => {
          let model = video;

          if (!model?.isInitialized?.() || !model?.player) {
            model = (await init()) ?? model;
          }

          if (gen !== generation || !model?.isInitialized?.() || model.isDestroyed?.() || !model.player) {
            return;
          }

          await model.player.play().catch(() => {});
        })();
      });
    };

    const scheduleTryPlay = () => {
      tryPlayIfActiveSlide();
      requestAnimationFrame(() => {
        requestAnimationFrame(tryPlayIfActiveSlide);
      });
    };

    swiper.on('slideChangeTransitionEnd', tryPlayIfActiveSlide);
    swiper.on('slideChange', tryPlayIfActiveSlide);
    swiper.on('loopFix', tryPlayIfActiveSlide);
    swiper.on('afterInit', scheduleTryPlay);
    swiper.on('slidesUpdated', tryPlayIfActiveSlide);

    if (swiper.initialized) {
      scheduleTryPlay();
    }

    return () => {
      generation += 10000;
      swiper.off('slideChangeTransitionEnd', tryPlayIfActiveSlide);
      swiper.off('slideChange', tryPlayIfActiveSlide);
      swiper.off('loopFix', tryPlayIfActiveSlide);
      swiper.off('afterInit', scheduleTryPlay);
      swiper.off('slidesUpdated', tryPlayIfActiveSlide);
    };
  }, [swiper, enabled, containerRef, video, init]);
}

